import axios from 'axios';
import '../axios'; // Import configured axios instance
import EventBus from 'eventing-bus';
import {
    setIntegrations,
    setOAuthCallbackSuccess,
    setOAuthCallbackError,
    setIntegrationStatus,
    setIntegrationStatusError,
    setIntegrationTools,
    setNodeToolsSuccess,
    setNodeToolsError
} from './index';
import { all, takeEvery, call, put, select } from 'redux-saga/effects';

function* getIntegrations() {
    const { error, response } = yield call(getCall, '/integration');
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) yield put(setIntegrations(response['data']['body']));
};

function* createIntegration({ payload }) {
    const { onSuccess, onFailure, ...integrationData } = payload;
    const { error, response } = yield call(postCall, { path: '/integration', payload: integrationData });

    if (error) {
        const errorMessage = error['response']['data']['message'];
        yield put({ type: 'CREATE_INTEGRATION_FAILURE', payload: errorMessage });
        if (onFailure) onFailure(errorMessage);
    } else if (response) {
        // Check if this is an OAuth integration that returns an auth URL
        if (response['data']['body']['authUrl']) {
            // All OAuth integrations use same-window redirect for consistent UX
            window.location.href = response['data']['body']['authUrl'];
            return;
        } else {
            // For non-OAuth integrations (API keys, etc.), refresh the list and dispatch success
            yield put({ type: 'CREATE_INTEGRATION_SUCCESS' });
            yield put({ type: 'GET_INTEGRATIONS' });
            if (onSuccess) onSuccess(response['data']['body']);
        }
    }
}

function* deleteIntegration({ payload: id }) {
    const { error, response } = yield call(postCall, { path: `/integration/delete/${id}`, payload: {} });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        EventBus.publish('success', 'Integration disconnected successfully');
        yield put({ type: 'GET_INTEGRATIONS' });
        // Refresh status for common integrations
        yield put({ type: 'GET_INTEGRATION_STATUS', payload: { toolName: 'gmail' } });
        yield put({ type: 'GET_INTEGRATION_STATUS', payload: { toolName: 'twilio' } });
    }
}

function* disconnectGmail() {
    const state = yield select();
    const integrations = state['Integration']['integrations'];
    const gmailIntegration = integrations.find(int => int['name'] === 'Gmail' && int['isConnected']);
    if (!gmailIntegration) return EventBus.publish('error', 'Gmail integration not found');
    if (!window.confirm('Are you sure you want to disconnect Gmail? This will remove access to all Gmail features.')) return;
    yield put({ type: 'DELETE_INTEGRATION', payload: gmailIntegration['_id'] });
}

// Generic OAuth Callback Saga (by provider)
function* handleOAuthCallback({ payload }) {
    const { provider, code, state } = payload;
    const normalizedProvider = provider.toLowerCase();
    try {
        const { error, response } = yield call(postCall, { path: `/integration/${normalizedProvider}/callback`, payload: { code, state }});
        if (error) {
            const errorMessage = error?.['response']?.['data']?.['message'] || `Failed to connect ${provider}`;
            yield put(setOAuthCallbackError(normalizedProvider, errorMessage));
        } else if (response && response['data']?.['code'] === 200) {
            const body = response['data']?.['body'];
            const email = body?.['email'] || `${provider} account`;
            const metadata = body?.['metadata'] || null;
            yield put(setOAuthCallbackSuccess(normalizedProvider, email, metadata));
            yield put({ type: 'GET_INTEGRATIONS' });
            // Refresh sales accounts if integration was auto-linked
            if (metadata?.salesAccountId) yield put({ type: 'GET_SALES_ACCOUNTS' });
        } else yield put(setOAuthCallbackError(normalizedProvider, 'Failed to process OAuth callback'));
    } catch (err) { yield put(setOAuthCallbackError(normalizedProvider, `Failed to connect ${provider}. Please try again.`)) }
}

// Generic Integration Status Saga (by toolName - supports comma-separated tools)
function* getIntegrationStatusSaga({ payload }) {
    const { toolName } = payload;
    const normalizedToolName = toolName.toLowerCase().replace(/\s+/g, '');
    const { error, response } = yield call(getCall, `/integration/status/${normalizedToolName}`);
    if (error) {
        const errorMessage = error?.['response']?.['data']?.['message'] || `Failed to get ${toolName} status`;
        
        // For multiple tools, set error for each
        const tools = normalizedToolName.split(',');
        for (const tool of tools) yield put(setIntegrationStatusError(tool.trim(), errorMessage));
    } else if (response) {
        // API returns { gmail: {...}, whatsapp: {...} } for multiple tools
        const body = response['data']['body'];
        const tools = normalizedToolName.split(',');

        // Dispatch status for each tool in the response
        for (const tool of tools) {
            const trimmedTool = tool.trim();
            if (body[trimmedTool]) yield put(setIntegrationStatus(trimmedTool, body[trimmedTool]));
        }
    }
}

// Integration Service Sagas
function* getIntegrationTools() {
    const { error, response } = yield call(getCall, '/integration/tool');
    if (error) EventBus.publish('error', error?.response?.data?.message || 'Failed to get integration tools');
    else if (response) yield put(setIntegrationTools(response.data.body));
}

// Node Tools Sagas
function* getNodeTools() {
    const { error, response } = yield call(getCall, '/integration/nodes');
    if (error) {
        const errorMessage = error?.['response']?.['data']?.['message'] || 'Failed to get node tools';
        yield put(setNodeToolsError(errorMessage));
        EventBus.publish('error', errorMessage);
    } else if (response) {
        // The API returns grouped nodes, we need to flatten them
        const flatNodes = [];
        const groupedNodes = response['data']['body'] || {};

        // Extract all nodes from the grouped structure
        Object.values(groupedNodes).forEach(categoryNodes => {
            if (Array.isArray(categoryNodes)) flatNodes.push(...categoryNodes)
        })

        yield put(setNodeToolsSuccess(flatNodes))
    }
}

function* getNodeDefinition({ payload }) {
    const { nodeType } = payload;
    const { error, response } = yield call(getCall, `/integration/nodes/${nodeType}`);
    if (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to get node definition';
        EventBus.publish('error', errorMessage);
    } else if (response) {
        // Node definition fetched successfully - could be used for specific node configuration
        return response['data']['body'];
    }
}

// Generic Token Refresh Saga (by toolName)
function* refreshIntegrationTokenSaga({ payload }) {
    const { toolName } = payload;
    const normalizedToolName = toolName.toLowerCase().replace(/\s+/g, '');
    const { error, response } = yield call(postCall, { path: `/integration/${normalizedToolName}/refresh`, payload: {} });
    if (error) {
        const errorMessage = error?.response?.data?.message || `Failed to refresh ${toolName} token`;
        yield put({ type: 'REFRESH_INTEGRATION_TOKEN_FAILURE', payload: { toolName: normalizedToolName, error: errorMessage } });
        EventBus.publish('error', errorMessage);
    } else if (response) {
        yield put({ type: 'REFRESH_INTEGRATION_TOKEN_SUCCESS', payload: { toolName: normalizedToolName } });
        EventBus.publish('success', `${toolName} token refreshed successfully`);
        yield put({ type: 'GET_INTEGRATIONS' });
    }
}

// Test Connection Saga (by integrationId)
function* testIntegrationConnectionSaga({ payload: integrationId }) {
    const { error, response } = yield call(getCall, `/integration/status/${integrationId}?test=true`);
    if (error) {
        const errorMessage = error?.['response']?.['data']?.['message'] || 'Connection test failed';
        yield put({ type: 'TEST_INTEGRATION_CONNECTION_FAILURE', payload: { integrationId, error: errorMessage } });
        EventBus.publish('error', errorMessage);
    } else if (response) {
        yield put({ type: 'TEST_INTEGRATION_CONNECTION_SUCCESS', payload: { integrationId } });
        EventBus.publish('success', 'Connection test successful');
    }
}

// Available Integrations Saga
function* getAvailableIntegrations({ payload }) {
    const { toolName } = payload;
    const { error, response } = yield call(getCall, `/integration/available/${toolName}`);
    if (error) {
        const errorMessage = error?.['response']?.['data']?.['message'] || 'Failed to get available integrations';
        yield put({ type: 'SET_AVAILABLE_INTEGRATIONS_ERROR', payload: errorMessage });
        EventBus.publish('error', errorMessage);
    } else if (response) {
        yield put({
            type: 'SET_AVAILABLE_INTEGRATIONS',
            payload: {
                total: response['data']['body'].total || 0,
                toolName: response['data']['body']['toolName'],
                ownIntegrations: response['data']['body']['ownIntegrations'] || [],
                publicIntegrations: response['data']['body']['publicIntegrations'] || [],
            }
        });
    }
}

// Update Integration Visibility Saga
function* updateIntegrationVisibility({ payload }) {
    const { integrationId, visibilityType } = payload;
    const { error, response } = yield call(postCall, { path: `/integration/update/${integrationId}`, payload: { visibilityType }});
    if (error) {
        EventBus.publish('error', error?.response?.data?.message || 'Failed to update integration visibility');
    } else if (response) {
        EventBus.publish('success', `Integration visibility updated to ${visibilityType}`);
        // Refresh integrations list
        yield put({ type: 'GET_INTEGRATIONS' });
    }
}

// Set Primary Integration Saga
function* setPrimaryIntegration({ payload }) {
    const { integrationId } = payload;
    const { error, response } = yield call(postCall, {
        path: `/integration/set-primary/${integrationId}`,
        payload: {}
    });
    if (error) {
        EventBus.publish('error', error?.response?.data?.message || 'Failed to set primary integration');
    } else if (response) {
        EventBus.publish('success', 'Primary account updated successfully');
        // Refresh integrations list
        yield put({ type: 'GET_INTEGRATIONS' });
    }
}

// ============================================================================
// SALES ACCOUNT SAGAS
// ============================================================================

// Get all sales accounts
function* getSalesAccounts() {
    const { error, response } = yield call(getCall, '/integration/sales-accounts');
    if (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch sales accounts';
        yield put({ type: 'SET_SALES_ACCOUNTS_ERROR', payload: errorMessage });
        EventBus.publish('error', errorMessage);
    } else if (response) {
        yield put({ type: 'SET_SALES_ACCOUNTS', payload: response.data.body });
    }
}

// Create sales account
function* createSalesAccountSaga({ payload }) {
    const { onSuccess, onFailure, ...accountData } = payload;
    const { error, response } = yield call(postCall, {
        path: '/integration/sales-accounts',
        payload: accountData
    });
    if (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to create sales account';
        yield put({ type: 'CREATE_SALES_ACCOUNT_ERROR', payload: errorMessage });
        EventBus.publish('error', errorMessage);
        if (onFailure) onFailure(errorMessage);
    } else if (response) {
        yield put({ type: 'CREATE_SALES_ACCOUNT_SUCCESS', payload: response.data.body });
        EventBus.publish('success', 'Sales account created successfully');
        if (onSuccess) onSuccess(response.data.body);
    }
}

// Update sales account
function* updateSalesAccountSaga({ payload }) {
    const { id, data } = payload;
    const { onSuccess, onFailure, ...accountData } = data;
    const { error, response } = yield call(postCall, {
        path: `/integration/sales-accounts/update/${id}`,
        payload: accountData
    });
    if (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to update sales account';
        yield put({ type: 'UPDATE_SALES_ACCOUNT_ERROR', payload: errorMessage });
        EventBus.publish('error', errorMessage);
        if (onFailure) onFailure(errorMessage);
    } else if (response) {
        yield put({ type: 'UPDATE_SALES_ACCOUNT_SUCCESS', payload: response.data.body });
        EventBus.publish('success', 'Sales account updated successfully');
        if (onSuccess) onSuccess(response.data.body);
    }
}

// Delete sales account
function* deleteSalesAccountSaga({ payload: id }) {
    const { error, response } = yield call(postCall, {
        path: `/integration/sales-accounts/delete/${id}`,
        payload: {}
    });
    if (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to delete sales account';
        yield put({ type: 'DELETE_SALES_ACCOUNT_ERROR', payload: errorMessage });
        EventBus.publish('error', errorMessage);
    } else if (response) {
        yield put({ type: 'DELETE_SALES_ACCOUNT_SUCCESS', payload: id });
        EventBus.publish('success', 'Sales account deleted successfully');
    }
}

// Set primary sales account
function* setPrimarySalesAccountSaga({ payload: id }) {
    const { error, response } = yield call(postCall, {
        path: `/integration/sales-accounts/set-primary/${id}`,
        payload: {}
    });
    if (error) {
        EventBus.publish('error', error?.response?.data?.message || 'Failed to set primary sales account');
    } else if (response) {
        EventBus.publish('success', 'Primary sales account updated');
        // Refresh the list to get updated primary status
        yield put({ type: 'GET_SALES_ACCOUNTS' });
    }
}

// Link integration to sales account
function* linkIntegrationToSalesAccountSaga({ payload }) {
    const { salesAccountId, integrationId } = payload;
    const { error, response } = yield call(postCall, {
        path: '/integration/sales-accounts/link-integration',
        payload: { salesAccountId, integrationId }
    });
    if (error) {
        EventBus.publish('error', error?.response?.data?.message || 'Failed to link integration');
    } else if (response) {
        EventBus.publish('success', 'Integration linked successfully');
        // Refresh sales accounts to get updated integrations
        yield put({ type: 'GET_SALES_ACCOUNTS' });
        // Also refresh integrations list
        yield put({ type: 'GET_INTEGRATIONS' });
    }
}

// Unlink integration from sales account
function* unlinkIntegrationFromSalesAccountSaga({ payload }) {
    const { integrationId } = payload;
    const { error, response } = yield call(postCall, {
        path: '/integration/sales-accounts/unlink-integration',
        payload: { integrationId }
    });
    if (error) {
        EventBus.publish('error', error?.response?.data?.message || 'Failed to unlink integration');
    } else if (response) {
        EventBus.publish('success', 'Integration unlinked successfully');
        // Refresh sales accounts to get updated integrations
        yield put({ type: 'GET_SALES_ACCOUNTS' });
        // Also refresh integrations list
        yield put({ type: 'GET_INTEGRATIONS' });
    }
}

// ============================================================================
// EMAIL SETTINGS SAGAS
// ============================================================================

// Get email settings for an integration
function* getEmailSettingsSaga({ payload }) {
    const { integrationId } = payload;
    const { error, response } = yield call(getCall, `/integration/email-settings/${integrationId}`);
    if (error) EventBus.publish('error', error?.response?.data?.message || 'Failed to get email settings');
    else if (response) yield put({ type: 'SET_EMAIL_SETTINGS', payload: { integrationId, settings: response.data.body.emailSettings } });
}

// Update email settings for an integration
function* updateEmailSettingsSaga({ payload }) {
    const { integrationId, settings, onSuccess } = payload;
    const { error, response } = yield call(postCall, { path: `/integration/email-settings/${integrationId}`, payload: { emailSettings: settings } });
    if (error) EventBus.publish('error', error?.response?.data?.message || 'Failed to update email settings');
    else if (response) {
        yield put({ type: 'SET_EMAIL_SETTINGS', payload: { integrationId, settings: response.data.body.emailSettings } });
        EventBus.publish('success', 'Email settings updated successfully');
        if (onSuccess) onSuccess();
    }
}

// Update email settings for multiple integrations in parallel
function* updateEmailSettingsBulkSaga({ payload }) {
    const { integrationIds, settings, onSuccess } = payload;
    const results = yield Promise.all(
        integrationIds.map(integrationId =>
            postCall({ path: `/integration/email-settings/${integrationId}`, payload: { emailSettings: settings } })
        )
    );
    for (let i = 0; i < results.length; i++) {
        const { error, response } = results[i];
        if (error) EventBus.publish('error', error?.response?.data?.message || 'Failed to update email settings');
        else if (response) yield put({ type: 'SET_EMAIL_SETTINGS', payload: { integrationId: integrationIds[i], settings: response['data']['body']['emailSettings'] } });
    }
    if (onSuccess) onSuccess();
}

// Run inbox placement test
function* runPlacementTestSaga({ payload: integrationId, callback }) {
    const { error, response } = yield call(postCall, { path: `/integration/placement-test/${integrationId}`, payload: {} });
    if (error) {
        if (callback) callback(null, error);
    } else if (response) {
        const result = response['data']['body'];
        if (callback) callback(result);
    }
}

// Get domain health (SPF, DKIM, DMARC) for an integration
function* getDomainHealthSaga({ payload: integrationId, callback }) {
    const { error, response } = yield call(getCall, `/integration/domain-health/${integrationId}`);
    if (error) {
        if (callback) callback(null, error);
    } else if (response) {
        const result = response['data']['body'];
        if (callback) callback(result);
    }
}

// ============================================================================
// LINKEDIN SAGAS
// ============================================================================

function* createLinkedInAccountSaga({ payload }) {
    const { onSuccess, onFailure, ...data } = payload;
    const { error, response } = yield call(postCall, { path: '/integration/linkedin/create', payload: data });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Failed to create LinkedIn account';
        EventBus.publish('error', msg);
        if (onFailure) onFailure(msg);
    } else if (response) {
        EventBus.publish('success', 'LinkedIn account created');
        yield put({ type: 'GET_INTEGRATIONS' });
        if (onSuccess) onSuccess(response['data']['body']);
    }
}

function* initiateLinkedInLoginSaga({ payload }) {
    const { id, onSuccess, onFailure, ...data } = payload;
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: true, error: null } } });
    const { error, response } = yield call(postCall, { path: `/integration/linkedin/login/${id}`, payload: data });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Failed to initiate LinkedIn login';
        EventBus.publish('error', msg);
        yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: false, error: msg } } });
        if (onFailure) onFailure(msg);
    } else if (response) {
        yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: false, error: null } } });
        yield put({ type: 'GET_INTEGRATIONS' });
        if (onSuccess) onSuccess(response['data']['body']);
    }
}

function* verifyLinkedInCodeSaga({ payload }) {
    const { id, code, onSuccess, onFailure } = payload;
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: true, error: null } } });
    const { error, response } = yield call(postCall, { path: `/integration/linkedin/verify/${id}`, payload: { code } });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Verification failed';
        EventBus.publish('error', msg);
        yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: false, error: msg } } });
        if (onFailure) onFailure(msg);
    } else if (response) {
        EventBus.publish('success', 'LinkedIn verification submitted');
        yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: false, error: null } } });
        yield put({ type: 'GET_INTEGRATIONS' });
        if (onSuccess) onSuccess(response['data']['body']);
    }
}

function* enrichLinkedInContactSaga({ payload }) {
    const { onSuccess, onFailure, ...data } = payload;
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id: 'enrich', loginState: { loading: true, error: null } } });
    const { error, response } = yield call(postCall, { path: '/integration/linkedin/enrich-contact', payload: data });
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id: 'enrich', loginState: { loading: false, error: null } } });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Enrichment failed';
        EventBus.publish('error', msg);
        if (onFailure) onFailure(msg);
    } else if (response) {
        EventBus.publish('success', 'LinkedIn enrichment started — contact will update shortly');
        if (onSuccess) onSuccess();
    }
}

function* resendLinkedInCodeSaga({ payload }) {
    const { id, onSuccess, onFailure } = payload;
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: true, error: null } } });
    const { error, response } = yield call(postCall, { path: `/integration/linkedin/resend-code/${id}`, payload: {} });
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: false, error: null } } });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Failed to resend code';
        if (onFailure) onFailure(msg);
    } else if (response) {
        if (onSuccess) onSuccess();
    }
}

function* cancelLinkedInVerificationSaga({ payload }) {
    const { id, onSuccess, onFailure } = payload;
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: true, error: null } } });
    const { error, response } = yield call(postCall, { path: `/integration/linkedin/cancel-verify/${id}`, payload: {} });
    yield put({ type: 'SET_LINKEDIN_LOGIN_STATE', payload: { id, loginState: { loading: false, error: null } } });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Failed to cancel';
        if (onFailure) onFailure(msg);
    } else if (response) {
        yield put({ type: 'GET_INTEGRATIONS' });
        if (onSuccess) onSuccess();
    }
}

function* connectSmtpAccountSaga({ payload }) {
    const { onSuccess, onFailure, ...data } = payload;
    const { error, response } = yield call(postCall, { path: '/integration/smtp/connect', payload: data });
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Failed to connect email account';
        EventBus.publish('error', msg);
        if (onFailure) onFailure(msg);
    } else if (response) {
        EventBus.publish('success', 'Email account connected successfully');
        yield put({ type: 'GET_INTEGRATIONS' });
        if (onSuccess) onSuccess(response['data']['body']);
    }
}

function* testSmtpConnectionSaga({ payload }) {
    const { id, onSuccess, onFailure } = payload;
    const { error, response } = yield call(getCall, `/integration/status/${id}?test=true`);
    if (error) {
        const msg = error?.['response']?.['data']?.['message'] || 'Connection test failed';
        EventBus.publish('error', msg);
        if (onFailure) onFailure(msg);
    } else if (response) {
        EventBus.publish('success', 'Connection verified');
        if (onSuccess) onSuccess(response['data']['body']);
    }
}

function* actionWatcher() {
    yield takeEvery('GET_INTEGRATIONS', getIntegrations);
    yield takeEvery('CREATE_INTEGRATION', createIntegration);
    yield takeEvery('DELETE_INTEGRATION', deleteIntegration);
    yield takeEvery('DISCONNECT_GMAIL', disconnectGmail);
    yield takeEvery('HANDLE_OAUTH_CALLBACK', handleOAuthCallback);

    // Generic Integration Status (by toolName)
    yield takeEvery('GET_INTEGRATION_STATUS', getIntegrationStatusSaga);

    // Integration Services
    yield takeEvery('GET_INTEGRATION_TOOLS', getIntegrationTools);

    // Generic Token Refresh and Test Connection (by toolName)
    yield takeEvery('REFRESH_INTEGRATION_TOKEN', refreshIntegrationTokenSaga);
    yield takeEvery('TEST_INTEGRATION_CONNECTION', testIntegrationConnectionSaga);

    // Node Tools
    yield takeEvery('GET_NODE_TOOLS', getNodeTools);
    yield takeEvery('GET_NODE_DEFINITION', getNodeDefinition);

    // Available Integrations (for dropdowns)
    yield takeEvery('GET_AVAILABLE_INTEGRATIONS', getAvailableIntegrations);
    yield takeEvery('UPDATE_INTEGRATION_VISIBILITY', updateIntegrationVisibility);
    yield takeEvery('SET_PRIMARY_INTEGRATION', setPrimaryIntegration);

    // Sales Accounts
    yield takeEvery('GET_SALES_ACCOUNTS', getSalesAccounts);
    yield takeEvery('CREATE_SALES_ACCOUNT', createSalesAccountSaga);
    yield takeEvery('UPDATE_SALES_ACCOUNT', updateSalesAccountSaga);
    yield takeEvery('DELETE_SALES_ACCOUNT', deleteSalesAccountSaga);
    yield takeEvery('SET_PRIMARY_SALES_ACCOUNT', setPrimarySalesAccountSaga);
    yield takeEvery('LINK_INTEGRATION_TO_SALES_ACCOUNT', linkIntegrationToSalesAccountSaga);
    yield takeEvery('UNLINK_INTEGRATION_FROM_SALES_ACCOUNT', unlinkIntegrationFromSalesAccountSaga);

    // Email Settings
    yield takeEvery('GET_EMAIL_SETTINGS', getEmailSettingsSaga);
    yield takeEvery('UPDATE_EMAIL_SETTINGS', updateEmailSettingsSaga);
    yield takeEvery('UPDATE_EMAIL_SETTINGS_BULK', updateEmailSettingsBulkSaga);

    // Domain Health
    yield takeEvery('GET_DOMAIN_HEALTH', getDomainHealthSaga);

    // Inbox Placement Test
    yield takeEvery('RUN_PLACEMENT_TEST', runPlacementTestSaga);

    // LinkedIn
    yield takeEvery('CREATE_LINKEDIN_ACCOUNT', createLinkedInAccountSaga);
    yield takeEvery('INITIATE_LINKEDIN_LOGIN', initiateLinkedInLoginSaga);
    yield takeEvery('VERIFY_LINKEDIN_CODE', verifyLinkedInCodeSaga);
    yield takeEvery('RESEND_LINKEDIN_CODE', resendLinkedInCodeSaga);
    yield takeEvery('ENRICH_LINKEDIN_CONTACT', enrichLinkedInContactSaga);
    yield takeEvery('CANCEL_LINKEDIN_VERIFICATION', cancelLinkedInVerificationSaga);

    // Gmail SMTP / App Password
    yield takeEvery('CONNECT_SMTP_ACCOUNT', connectSmtpAccountSaga);
    yield takeEvery('TEST_SMTP_CONNECTION', testSmtpConnectionSaga);
}

export default function* rootSaga() {
    yield all([actionWatcher()]);
}

function postCall({ path, payload }) {
    return axios
        .post(path, payload)
        .then(response => ({ response }))
        .catch(error => {
            if (error.response?.status === 401) EventBus.publish('tokenExpired');
            return { error };
        });
}

function getCall(path) {
    return axios
        .get(path)
        .then(response => ({ response }))
        .catch(error => {
            if (error.response?.status === 401) EventBus.publish('tokenExpired');
            return { error };
        });
}

function deleteCall(path) {
    return axios
        .delete(path)
        .then(response => ({ response }))
        .catch(error => {
            if (error.response?.status === 401) EventBus.publish('tokenExpired');
            return { error };
        });
}

function putCall({ path, payload }) {
    return axios
        .put(path, payload)
        .then(response => ({ response }))
        .catch(error => {
            if (error.response?.status === 401) EventBus.publish('tokenExpired');
            return { error };
        });
}